// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Sped.tsx
//  SPED — geração e download de EFD-ICMS/IPI e EFD-Contribuições.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Download, FileText, FileCode, Loader2, Info, Database, History } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/hooks/useAuth';
import { usePermissoes } from '@/contexts/PermissoesContext';
import { FORMULARIOS } from '@/domain/formularios';
import { apiClient } from '@/lib/apiClient';
import { toast } from '@/components/ui/sonner';

// ── helpers ──────────────────────────────────────────────────────────────────

const today   = () => new Date().toISOString().slice(0, 10);
const firstDayOfMonth = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().slice(0, 10);
};

async function baixarSped(
  endpoint: string,
  body: object,
  nomeArquivo: string,
  setLoading: (v: boolean) => void
) {
  setLoading(true);
  try {
    const resp = await apiClient.post(endpoint, body, { responseType: 'blob' });
    const url  = window.URL.createObjectURL(new Blob([resp.data]));
    const a    = document.createElement('a');
    a.href     = url;
    a.download = nomeArquivo;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Arquivo gerado com sucesso!');
  } catch (err: unknown) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const msg = (err as any)?.response?.data?.message ?? 'Erro ao gerar arquivo SPED.';
    toast.error('Erro', { description: msg });
  } finally {
    setLoading(false);
  }
}

// ── Tab EFD-ICMS/IPI ──────────────────────────────────────────────────────────

function TabEfdIcmsIpi({ empresaId }: { empresaId: number }) {
  const [dtInicio,      setDtInicio]      = useState(firstDayOfMonth());
  const [dtFim,         setDtFim]         = useState(today());
  const [perfil,        setPerfil]        = useState('A');
  const [finalidade,    setFinalidade]    = useState('0');
  const [incluirBlocoE, setIncluirBlocoE] = useState(true);
  const [incluirBlocoH, setIncluirBlocoH] = useState(false);
  const [dtInventario,  setDtInventario]  = useState('');
  const [incluirBlocoK, setIncluirBlocoK] = useState(false);
  const [loading,       setLoading]       = useState(false);

  const handleGerar = () =>
    baixarSped(
      '/api/sped/efd-icms-ipi',
      {
        empresaId,
        dtInicio,
        dtFim,
        perfil,
        finalidade: Number(finalidade),
        incluirBlocoE,
        incluirBlocoH,
        dtInventario: incluirBlocoH && dtInventario ? dtInventario : null,
        incluirBlocoK,
      },
      `EFD_ICMSIPI_${dtInicio.slice(0, 7).replace('-', '')}.txt`,
      setLoading
    );

  const blocosAtivos = [
    '0 — Abertura / Empresa',
    'C — Notas Fiscais',
    incluirBlocoE && 'E — Apuração ICMS/IPI',
    incluirBlocoH && 'H — Inventário',
    incluirBlocoK && 'K — Controle Estoque',
    '9 — Encerramento',
  ].filter(Boolean) as string[];

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Gera o arquivo EFD-ICMS/IPI com base nas notas fiscais autorizadas no período.
          Layout EFD-ICMS/IPI v3.1.9.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="space-y-1">
          <Label>Data inicial</Label>
          <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Data final</Label>
          <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Perfil</Label>
          <Select value={perfil} onValueChange={setPerfil}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="A">Perfil A — Completo</SelectItem>
              <SelectItem value="B">Perfil B — Simplificado</SelectItem>
              <SelectItem value="C">Perfil C — Simplificado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Finalidade</Label>
          <Select value={finalidade} onValueChange={setFinalidade}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Original</SelectItem>
              <SelectItem value="1">Substituta</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Blocos opcionais */}
      <div className="rounded-lg border p-4 space-y-4">
        <p className="text-sm font-medium text-foreground">Blocos opcionais</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="blocoE"
              checked={incluirBlocoE}
              onCheckedChange={(v) => setIncluirBlocoE(Boolean(v))}
            />
            <div>
              <Label htmlFor="blocoE" className="cursor-pointer font-medium">
                Bloco E — Apuração ICMS/IPI
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Calcula débitos (saídas) e créditos (entradas) do período.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="blocoH"
              checked={incluirBlocoH}
              onCheckedChange={(v) => setIncluirBlocoH(Boolean(v))}
            />
            <div>
              <Label htmlFor="blocoH" className="cursor-pointer font-medium">
                Bloco H — Inventário
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Saldo atual do estoque com custo médio.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="blocoK"
              checked={incluirBlocoK}
              onCheckedChange={(v) => setIncluirBlocoK(Boolean(v))}
            />
            <div>
              <Label htmlFor="blocoK" className="cursor-pointer font-medium">
                Bloco K — Controle de Estoque
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Posição do estoque ao final do período.
              </p>
            </div>
          </div>
        </div>

        {incluirBlocoH && (
          <div className="space-y-1 max-w-xs">
            <Label htmlFor="dtInventario">Data base do inventário (opcional)</Label>
            <Input
              id="dtInventario"
              type="date"
              value={dtInventario}
              onChange={(e) => setDtInventario(e.target.value)}
              placeholder={dtFim}
            />
            <p className="text-xs text-muted-foreground">Se vazio, usa a data final do período.</p>
          </div>
        )}
      </div>

      <div className="rounded-lg border p-4 bg-muted/30 space-y-2 text-sm">
        <p className="font-medium text-foreground">Blocos que serão gerados:</p>
        <div className="flex flex-wrap gap-2">
          {blocosAtivos.map((b) => (
            <Badge key={b} variant="outline" className="font-mono text-xs">{b}</Badge>
          ))}
        </div>
      </div>

      <Button onClick={handleGerar} disabled={loading || !dtInicio || !dtFim} className="gap-2">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {loading ? 'Gerando…' : 'Gerar e Baixar EFD-ICMS/IPI'}
      </Button>
    </div>
  );
}

// ── Tab EFD-Contribuições ─────────────────────────────────────────────────────

function TabEfdContribuicoes({ empresaId }: { empresaId: number }) {
  const [dtInicio,   setDtInicio]   = useState(firstDayOfMonth());
  const [dtFim,      setDtFim]      = useState(today());
  const [finalidade, setFinalidade] = useState('0');
  const [loading,    setLoading]    = useState(false);

  const handleGerar = () =>
    baixarSped(
      '/api/sped/efd-contribuicoes',
      { empresaId, dtInicio, dtFim, finalidade: Number(finalidade) },
      `EFD_Contribuicoes_${dtInicio.slice(0, 7).replace('-', '')}.txt`,
      setLoading
    );

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Gera o arquivo EFD-Contribuições (PIS/COFINS) com base nas notas fiscais
          do período. Layout EFD-Contribuições v006.
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="space-y-1">
          <Label>Data inicial</Label>
          <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Data final</Label>
          <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label>Finalidade</Label>
          <Select value={finalidade} onValueChange={setFinalidade}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Original</SelectItem>
              <SelectItem value="1">Substituta</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border p-4 bg-muted/30 space-y-2 text-sm">
        <p className="font-medium text-foreground">Blocos incluídos nesta versão:</p>
        <div className="flex flex-wrap gap-2">
          {['0 — Abertura / Empresa', 'C — Notas Fiscais (PIS/COFINS)', '9 — Encerramento'].map((b) => (
            <Badge key={b} variant="outline" className="font-mono text-xs">{b}</Badge>
          ))}
        </div>
        <p className="text-muted-foreground text-xs">
          Blocos F (Demais Documentos) e M (Apuração) serão adicionados em versão futura.
        </p>
      </div>

      <Button onClick={handleGerar} disabled={loading || !dtInicio || !dtFim} className="gap-2">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {loading ? 'Gerando…' : 'Gerar e Baixar EFD-Contribuições'}
      </Button>
    </div>
  );
}

// ── Tab Sintegra ──────────────────────────────────────────────────────────────

function TabSintegra({ empresaId }: { empresaId: number }) {
  const { temPermissao } = usePermissoes();
  const podeGerar = temPermissao(FORMULARIOS.SINTEGRA, 'incluir');
  const [dtInicio, setDtInicio]                     = useState(firstDayOfMonth());
  const [dtFim, setDtFim]                           = useState(today());
  const [finalidade, setFinalidade]                 = useState('1');
  const [natureza, setNatureza]                     = useState('3');
  const [incluirInventario, setIncluirInventario]   = useState(true);
  const [loading, setLoading]                       = useState(false);

  const handleGerar = () =>
    baixarSped(
      '/api/sped/sintegra',
      {
        empresaId,
        dtInicio,
        dtFim,
        finalidade: Number(finalidade),
        natureza: Number(natureza),
        incluirInventario,
      },
      `SINTEGRA_${dtInicio.replace(/-/g, '')}_${dtFim.replace(/-/g, '')}.txt`,
      setLoading,
    );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="sint-ini">Data Início</Label>
          <Input id="sint-ini" type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sint-fim">Data Fim</Label>
          <Input id="sint-fim" type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="sint-final">Finalidade</Label>
          <Select value={finalidade} onValueChange={setFinalidade}>
            <SelectTrigger id="sint-final"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 — Normal</SelectItem>
              <SelectItem value="2">2 — Retificação total</SelectItem>
              <SelectItem value="3">3 — Retificação parcial</SelectItem>
              <SelectItem value="5">5 — Desfazimento</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="sint-nat">Natureza das operações</Label>
          <Select value={natureza} onValueChange={setNatureza}>
            <SelectTrigger id="sint-nat"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 — Interestadual</SelectItem>
              <SelectItem value="2">2 — Interestadual + intermunicipal</SelectItem>
              <SelectItem value="3">3 — Todas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox id="sint-inv" checked={incluirInventario}
                  onCheckedChange={(v) => setIncluirInventario(Boolean(v))} />
        <Label htmlFor="sint-inv" className="cursor-pointer">
          Incluir inventário (registro 74) com saldo na data fim
        </Label>
      </div>

      <div className="rounded-lg border p-4 bg-muted/30 space-y-2 text-sm">
        <p className="font-medium text-foreground">Registros incluídos:</p>
        <div className="flex flex-wrap gap-2">
          {['10 — Mestre', '11 — Endereço', '50 — NF-e', '60M/60A/60I — NFCe', '74 — Inventário', '75 — Produtos', '90 — Totalizador'].map((b) => (
            <Badge key={b} variant="outline" className="font-mono text-xs">{b}</Badge>
          ))}
        </div>
        <p className="text-muted-foreground text-xs">
          Layout flat ASCII 126 chars × linha, encoding ISO-8859-1, conforme Convênio ICMS 57/95.
        </p>
      </div>

      {!podeGerar && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Você não tem permissão para gerar o arquivo Sintegra. Solicite ao administrador
            a permissão de inclusão no formulário Sintegra.
          </AlertDescription>
        </Alert>
      )}

      <Button onClick={handleGerar} disabled={!podeGerar || loading || !dtInicio || !dtFim} className="gap-2">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {loading ? 'Gerando…' : 'Gerar e Baixar Sintegra'}
      </Button>
    </div>
  );
}

// ── Tab Histórico ─────────────────────────────────────────────────────────────

interface SpedHistoricoItem {
  id: number;
  empresaId: number;
  tipo: string;
  periodo: string;
  dtInicio: string;
  dtFim: string;
  dtGeracao: string;
  usuarioId?: number | null;
  hashMd5?: string | null;
  tamanhoBytes: number;
  nomeArquivo?: string | null;
  resumo?: string | null;
}

const TIPO_LABEL: Record<string, string> = {
  'efd-icms-ipi': 'EFD-ICMS/IPI',
  'efd-contribuicoes': 'EFD-Contribuições',
  'sintegra': 'Sintegra',
};

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function TabHistorico({ empresaId }: { empresaId: number }) {
  const [filtroTipo, setFiltroTipo] = useState<string>('todos');
  const [ano, setAno] = useState<number>(new Date().getFullYear());

  const { data = [], isLoading } = useQuery({
    queryKey: ['sped-historico', empresaId, filtroTipo, ano],
    queryFn: async () => {
      const params: Record<string, string | number> = { empresaId, ano };
      if (filtroTipo !== 'todos') params.tipo = filtroTipo;
      const { data } = await apiClient.get<SpedHistoricoItem[]>('/api/sped/historico', { params });
      return data;
    },
    enabled: empresaId > 0,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Tipo</Label>
          <Select value={filtroTipo} onValueChange={setFiltroTipo}>
            <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="efd-icms-ipi">EFD-ICMS/IPI</SelectItem>
              <SelectItem value="efd-contribuicoes">EFD-Contribuições</SelectItem>
              <SelectItem value="sintegra">Sintegra</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Ano</Label>
          <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 5 }).map((_, i) => {
                const a = new Date().getFullYear() - i;
                return <SelectItem key={a} value={String(a)}>{a}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? <Skeleton className="h-32" /> : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data geração</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Período</TableHead>
              <TableHead>Arquivo</TableHead>
              <TableHead className="text-right">Tamanho</TableHead>
              <TableHead>Resumo</TableHead>
              <TableHead className="font-mono text-xs">Hash MD5</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  Nenhuma geração registrada com os filtros atuais.
                </TableCell>
              </TableRow>
            ) : data.map((h) => (
              <TableRow key={h.id}>
                <TableCell className="text-sm">
                  {new Date(h.dtGeracao).toLocaleString('pt-BR')}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">{TIPO_LABEL[h.tipo] ?? h.tipo}</Badge>
                </TableCell>
                <TableCell className="font-mono text-sm">{h.periodo}</TableCell>
                <TableCell className="text-sm">{h.nomeArquivo}</TableCell>
                <TableCell className="text-right text-sm">{fmtBytes(h.tamanhoBytes)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{h.resumo}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {h.hashMd5?.slice(0, 12)}…
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────

const Sped = () => {
  const { user } = useAuth();
  const empresaId = user?.empresa?.id;

  if (!empresaId) {
    return (
      <div className="p-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>Empresa não identificada. Faça login novamente.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">SPED</h1>
        <p className="text-muted-foreground mt-1">Sistema Público de Escrituração Digital</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-blue-200 bg-blue-50/40 dark:bg-blue-950/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileCode className="h-4 w-4 text-blue-600" />
              EFD-ICMS/IPI
            </CardTitle>
            <CardDescription>Escrituração Fiscal Digital do ICMS e IPI — Mensal</CardDescription>
          </CardHeader>
        </Card>
        <Card className="border-green-200 bg-green-50/40 dark:bg-green-950/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-green-600" />
              EFD-Contribuições
            </CardTitle>
            <CardDescription>Escrituração Fiscal Digital de PIS/COFINS — Mensal</CardDescription>
          </CardHeader>
        </Card>
      </div>

      <Tabs defaultValue="icms-ipi">
        <TabsList>
          <TabsTrigger value="icms-ipi" className="gap-2">
            <FileCode className="h-4 w-4" />EFD-ICMS/IPI
          </TabsTrigger>
          <TabsTrigger value="contribuicoes" className="gap-2">
            <FileText className="h-4 w-4" />EFD-Contribuições
          </TabsTrigger>
          <TabsTrigger value="sintegra" className="gap-2">
            <Database className="h-4 w-4" />Sintegra
          </TabsTrigger>
          <TabsTrigger value="historico" className="gap-2">
            <History className="h-4 w-4" />Histórico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="icms-ipi">
          <Card>
            <CardHeader>
              <CardTitle>EFD-ICMS/IPI — Parâmetros de Geração</CardTitle>
            </CardHeader>
            <CardContent>
              <TabEfdIcmsIpi empresaId={empresaId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contribuicoes">
          <Card>
            <CardHeader>
              <CardTitle>EFD-Contribuições — Parâmetros de Geração</CardTitle>
            </CardHeader>
            <CardContent>
              <TabEfdContribuicoes empresaId={empresaId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sintegra">
          <Card>
            <CardHeader>
              <CardTitle>Sintegra (Convênio ICMS 57/95) — Parâmetros</CardTitle>
              <CardDescription>
                Validação de movimentações para fiscalização estadual. Aceita NF-e (modelo 55) e NFCe (modelo 65).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TabSintegra empresaId={empresaId} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historico">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de gerações</CardTitle>
              <CardDescription>
                Metadados de cada geração: período, hash MD5, tamanho. O arquivo em si é re-gerado sob demanda.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TabHistorico empresaId={empresaId} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Sped;
